import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Wand2, Loader2, BookOpen, Users, Briefcase, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

const PRESET_ICONS = {
  'default':     BookOpen,
  'beta-reader': Users,
  'co-author':   Sparkles,
  'publisher':   Briefcase,
};

/** Pre-built permission-grid templates an admin can apply in one click. */
export default function RolePresets({ onApplied }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  useEffect(() => {
    adminApi.rolePresets()
      .then((r) => setPresets(r.presets || []))
      .catch(() => toast.error('Could not load role presets.'))
      .finally(() => setLoading(false));
  }, []);

  const apply = async (id) => {
    setBusyId(id);
    try {
      await adminApi.applyRolePreset(id);
      toast.success(`Applied "${id}" role preset.`);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply preset.');
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  };

  if (loading) {
    return (
      <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> Loading presets…
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
        Role preset templates
      </h3>
      <p className="mt-1 text-[11.5px] text-muted-foreground">
        One-click grids for common collaboration patterns. Applying a preset overwrites your custom grid above.
      </p>
      <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-4">
        {presets.map((p) => {
          const Icon = PRESET_ICONS[p.id] || BookOpen;
          const isBusy = busyId === p.id;
          const isConfirming = confirmId === p.id;
          return (
            <div
              key={p.id}
              data-testid={`role-preset-${p.id}`}
              className="rounded-md border border-border bg-card p-3"
            >
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-secondary text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-[12.5px] font-semibold">{p.label}</div>
              </div>
              <div className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{p.desc}</div>
              {!isConfirming ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => setConfirmId(p.id)}
                  data-testid={`role-preset-apply-${p.id}`}
                  className="mt-3 h-7 w-full gap-1.5 text-[11px]"
                >
                  <Wand2 className="h-3 w-3" /> Apply preset
                </Button>
              ) : (
                <div className="mt-3 flex gap-1.5">
                  <Button
                    size="sm"
                    disabled={isBusy}
                    onClick={() => apply(p.id)}
                    data-testid={`role-preset-confirm-${p.id}`}
                    className="h-7 flex-1 gap-1 text-[10.5px]"
                  >
                    {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmId(null)}
                    className="h-7 flex-1 text-[10.5px]"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
